<template>
  <v-container fluid class="pa-4">
    <v-card elevation="2" rounded="lg">
      <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold">
            Repartos proveedores híbridos
          </div>

          <div class="text-body-2 text-medium-emphasis mt-1">
            Líneas pendientes asignadas a proveedores híbridos con contacto de almacén de Barcelona.
          </div>
        </div>

        <div class="d-flex ga-2 align-center flex-wrap">
          <v-select
            v-model="filters.delegacion_id"
            :items="delegaciones"
            label="Delegación"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details
            style="min-width: 180px"
          />

          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            prepend-icon="mdi-refresh"
            @click="fetchData"
          >
            Actualizar
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>

       <!--<v-alert
          type="info"
          variant="tonal"
          class="mb-4"
        >
          Primero se buscan proveedores con <strong>supplier_type = hybrid</strong>
          y después se cargan sus líneas pendientes de salida y distribución.
        </v-alert>-->

        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Proveedores híbridos
                </div>

                <div class="text-h5 font-weight-bold">
                  {{ providers.length }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Grupos
                </div>

                <div class="text-h5 font-weight-bold">
                  {{ groupedItems.length }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Folletos
                </div>

                <div class="text-h5 font-weight-bold">
                  {{ formatNumber(totalFolletos) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card
              variant="tonal"
              rounded="lg"
              :color="totalSinPuntoInicio > 0 ? 'warning' : undefined"
            >
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Sin punto inicio
                </div>

                <div class="text-h5 font-weight-bold">
                  {{ totalSinPuntoInicio }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-expansion-panels class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-title>
              Ver proveedores híbridos encontrados
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Proveedor</th>
                    <th>Email</th>
                    <th>Teléfono</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="provider in providers"
                    :key="provider.id"
                  >
                    <td>{{ provider.id }}</td>
                    <td>{{ provider.commercial_company_name || provider.name || '-' }}</td>
                    <td>{{ provider.email || '-' }}</td>
                    <td>{{ provider.phone || provider.mobile || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-text-field
          v-model="search"
          label="Buscar por proveedor, cliente, campaña, folleto, servicio o notas"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="mb-4"
        />

        <div class="d-flex justify-end align-center mb-4 ga-2">
          <v-chip
            v-if="selectedLineCount > 0"
            color="primary"
            variant="tonal"
            size="small"
          >
            {{ selectedLineCount }} línia/es seleccionada/es
          </v-chip>

          <v-btn
            color="success"
            variant="flat"
            prepend-icon="mdi-truck-check-outline"
            :loading="savingRexpedido"
            :disabled="selectedLineCount === 0"
            @click="marcarRexpedido"
          >
            Rexpedido
          </v-btn>
        </div>

        <v-data-table
          v-model:expanded="expanded"
          :headers="headers"
          :items="groupedItems"
          :search="search"
          :loading="loading"
          item-value="id"
          density="compact"
          class="elevation-1 rounded-lg main-groups-table"
          :items-per-page="50"
          show-expand
        >
          <template #item.partner_name="{ item }">
            <div class="font-weight-medium">
              {{ item.partner_name }}
            </div>
          </template>

          <template #item.customer_name="{ item }">
            <div>
              {{ item.customer_name }}
            </div>
          </template>

          <template #item.campaign_name="{ item }">
            <div>
              <div class="font-weight-bold">
                {{ item.campaign_name }} 
              </div>

              <div class="text-caption text-medium-emphasis">
                 Líneas: {{ item.line_count }}
              </div>
            </div>
          </template>

          <template #item.quantity="{ item }">
            <div class="text-right">
              <v-chip
                :color="getGroupQuantityColor(item.quantity)"
                variant="tonal"
                size="small"
                class="font-weight-bold"
              >
                {{ formatNumber(item.quantity) }}
              </v-chip>
            </div>
          </template>

          <template #item.start_date_min="{ item }">

              {{ formatDate(item.start_date_min) }}
          
          </template>

          <template #expanded-row="{ columns, item }">
            <tr class="expanded-group-row">
              <td :colspan="columns.length" class="pa-0">
                <v-card flat class="pa-4 expanded-group-card">
                  <div class="expanded-group-header mb-3">
                    <div>
                      <div class="text-subtitle-1 font-weight-bold">
                        {{ item.partner_name }}
                      </div>

                      <div class="text-caption text-medium-emphasis">
                        {{ item.customer_name }} · {{ item.campaign_name }} · ID campaña: {{ item.campaign_id }}
                      </div>
                    </div>

                    <v-chip color="primary" variant="tonal" size="small">
                      {{ formatNumber(item.quantity) }} folletos
                    </v-chip>
                  </div>

                  <v-data-table
                    :headers="lineHeaders"
                    :items="item.lines"
                    item-value="id"
                    density="compact"
                    class="elevation-0 inner-lines-table"
                    hide-default-footer
                  >
                    <template #item.selected="{ item: line }">
                      <v-checkbox-btn
                        :model-value="isLineSelected(line)"
                        color="success"
                        density="compact"
                        @update:model-value="toggleLineSelection(line)"
                      />
                    </template>

                    <template #item.flyer_reference="{ item: line }">
                      {{ line.flyer_reference || '-' }}
                    </template>

                    <template #item.service_type_name="{ item: line }">
                      <v-chip
                        size="x-small"
                        variant="tonal"
                        :color="getServiceColor(line.service_type_name)"
                      >
                        {{ line.service_type_name || '-' }}
                      </v-chip>
                    </template>

                    <template #item.supplier_note="{ item: line }">
                      <div class="text-caption">
                        {{ line.supplier_note || '-' }}
                      </div>
                    </template>

                    <template #item.quantity="{ item: line }">
                      <div class="text-right">
                        <v-chip
                          size="small"
                          color="success"
                          variant="tonal"
                          class="font-weight-bold"
                        >
                          {{ formatNumber(line.quantity) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #item.line_count="{ item: line }">
                      <div class="text-right">
                        {{ line.line_count || 1 }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </td>
            </tr>
          </template>

          <template #no-data>
            <div class="pa-6 text-center text-medium-emphasis">
              No hay líneas para proveedores híbridos.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const loading = ref(false)
const errorMessage = ref('')
const search = ref('')
const items = ref([])
const providers = ref([])
const expanded = ref([])
const selectedLineGroupIds = ref([])
const savingRexpedido = ref(false)

const filters = ref({
  delegacion_id: 453,
})

const delegaciones = [
  { title: 'Barcelona', value: 453 },
  { title: 'Madrid', value: 502 },
  { title: 'Tarragona', value: 2016 },
  { title: 'Guadalajara', value: 9784 },
]

const headers = [
  { title: 'Proveedor', key: 'partner_name', sortable: true },
  { title: 'Cliente', key: 'customer_name', sortable: true },
  { title: 'Campaña', key: 'campaign_name', sortable: true },
  { title: 'Fecha inicio', key: 'start_date_min', sortable: true },
  { title: 'Folletos totales', key: 'quantity', sortable: true, align: 'end' },
]

const lineHeaders = [
  { title: '', key: 'selected', sortable: false, width: 48 },
  { title: 'Ref. folleto', key: 'flyer_reference', sortable: true },
  { title: 'Servicio', key: 'service_type_name', sortable: true },
  { title: 'Notas línea', key: 'supplier_note', sortable: true },
  { title: 'Cantidad', key: 'quantity', sortable: true, align: 'end' },
  { title: 'Líneas', key: 'line_count', sortable: true, align: 'end' },
]

const groupedItems = computed(() => {
  const buckets = new Map()

  for (const item of items.value) {
    const partnerId = item.partner_id || 'sin_proveedor'
    const customerId = item.customer_id || 'sin_cliente'
    const campaignId = item.campaign_id || 'sin_campanya'

    const key = `${partnerId}|${customerId}|${campaignId}`

    if (!buckets.has(key)) {
      buckets.set(key, {
        id: key,

        partner_id: item.partner_id,
        partner_name: item.partner_name || 'Sin proveedor',

        customer_id: item.customer_id,
        customer_name: item.customer_name || 'Sin cliente',

        campaign_id: item.campaign_id,
        campaign_name: item.campaign_name || 'Sin campaña',
        start_date_min: item.start_date_min || item.start_date || null,

        quantity: 0,
        line_count: 0,

        search_text: '',

        lines: [],
      })
    }

    const group = buckets.get(key)

    group.quantity += Number(item.quantity || 0)
    group.line_count += Number(item.line_count || 0)
    group.lines.push(item)

    const itemStartDate = item.start_date_min || item.start_date || null

    if (
      itemStartDate &&
      (
        !group.start_date_min ||
        itemStartDate < group.start_date_min
      )
    ) {
      group.start_date_min = itemStartDate
    }

    group.search_text = [
      group.partner_name,
      group.customer_name,
      group.campaign_id,
      group.campaign_name,
      item.flyer_reference,
      item.service_type_name,
      item.supplier_note,
      item.quantity,
      item.line_count,
    ].filter(Boolean).join(' ')
  }

  return Array.from(buckets.values()).sort((a, b) => {
    const providerCompare = String(a.partner_name).localeCompare(String(b.partner_name), 'es')

    if (providerCompare !== 0) {
      return providerCompare
    }

    const clientCompare = String(a.customer_name).localeCompare(String(b.customer_name), 'es')

    if (clientCompare !== 0) {
      return clientCompare
    }

    return String(a.campaign_name).localeCompare(String(b.campaign_name), 'es')
  })
})

const totalFolletos = computed(() => {
  return items.value.reduce((total, item) => total + Number(item.quantity || 0), 0)
})

const totalSinPuntoInicio = computed(() => {
  return items.value.filter(item => {
    const puntInici = item.punt_inici_text || item.punt_inici || ''
    return !String(puntInici).trim()
  }).length
})

// Per guardar les reexpedicions
const selectedLineIds = computed(() => {
  return selectedLineGroupIds.value
    .flatMap(item => item.line_ids || [])
    .filter(Boolean)
})

const selectedLineCount = computed(() => {
  return selectedLineIds.value.length
})

function isLineSelected(line) {
  return selectedLineGroupIds.value.some(selected => selected.id === line.id)
}

function toggleLineSelection(line) {
  const exists = isLineSelected(line)

  if (exists) {
    selectedLineGroupIds.value = selectedLineGroupIds.value.filter(
      selected => selected.id !== line.id
    )

    return
  }

  selectedLineGroupIds.value = [
    ...selectedLineGroupIds.value,
    line,
  ]
}



async function fetchData() {
  loading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/admin/produccion/repartos-proveedores-hibridos', {
      params: {
        delegacion_id: filters.value.delegacion_id,
      },
    })

    providers.value = response.data?.providers || []

    items.value = (response.data?.data || []).map(item => ({
      ...item,

      partner_sort: item.partner_name || '',
      service_sort: item.service_type_name || '',

      searchable_text: [
        item.partner_name,
        item.campaign_id,
        item.campaign_name,
        item.customer_name,
        item.flyer_reference,
        item.service_type_name,
        item.supplier_note,
        item.quantity,
        item.line_count,
      ].filter(Boolean).join(' '),
    }))
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se ha podido cargar el listado de proveedores híbridos.'
  } finally {
    loading.value = false
  }
}

async function marcarRexpedido() {
  if (!selectedLineCount.value) {
    errorMessage.value = 'Selecciona almenys una línia.'
    return
  }

  const confirmar = window.confirm(
    `Vols marcar com a Rexpedido ${selectedLineCount.value} línia/es?`
  )

  if (!confirmar) {
    return
  }

  savingRexpedido.value = true
  errorMessage.value = ''

  try {
    await axios.post('/api/admin/produccion/repartos-proveedores-hibridos/reexpedir', {
      line_ids: selectedLineIds.value,
    })

    selectedLineGroupIds.value = []
    await fetchData()
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se han podido marcar las líneas como Rexpedido.'
  } finally {
    savingRexpedido.value = false
  }
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('es-ES')
}

function getServiceColor(serviceName) {
  const service = String(serviceName || '').toLowerCase()

  if (service.includes('tienda')) {
    return 'deep-purple'
  }

  if (service.includes('buzoneo')) {
    return 'blue'
  }

  if (service.includes('impres')) {
    return 'orange'
  }

  if (service.includes('rex')) {
    return 'teal'
  }

  if (service.includes('plv')) {
    return 'pink'
  }

  return 'grey'
}

function getGroupQuantityColor(quantity) {
  const total = Number(quantity || 0)

  if (total >= 50000) {
    return 'error'
  }

  if (total >= 20000) {
    return 'warning'
  }

  return 'success'
}


function formatDate(date) {
  if (!date) {
    return '-'
  }

  const cleanDate = String(date).slice(0, 10)
  const parts = cleanDate.split('-')

  if (parts.length !== 3) {
    return cleanDate
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.main-groups-table :deep(thead th) {
  background: #65c5cf !important;
  font-weight: 700 !important;
}

.main-groups-table :deep(tbody tr:hover) {
  background: #f8fbff !important;
}

.expanded-group-row {
  background: #eef5ff;
}

.expanded-group-card {
  border-left: 5px solid #1976d2;
  background: linear-gradient(90deg, #f4f8ff 0%, #ffffff 45%);
}

.expanded-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
}

.inner-lines-table {
  border: 1px solid #d6e4f5;
  border-radius: 10px;
  overflow: hidden;
}

.inner-lines-table :deep(thead th) {
  background: #f0f6ff !important;
  color: #2f4f6f !important;
  font-weight: 700 !important;
}

.inner-lines-table :deep(tbody tr:nth-child(even)) {
  background: #fafcff;
}

.inner-lines-table :deep(tbody tr:hover) {
  background: #eaf4ff !important;
}
</style>